import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { GalleryModal } from './GalleryModal';
import { Icon } from './Icon';
import { galleryItems } from '../data/gallery';
import type { ProjectGalleryItem } from '../types/gallery';

const GallerySection = styled.section`
  padding: 5rem 0;
  background-color: var(--surface-section);
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 75rem;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 0 1rem;
  }
`;

const SectionHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.heading};
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.text};

  @media (min-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 3rem;
  }

  .gradient-text {
    background: ${props => props.theme.gradients.primary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.25rem;
  color: ${props => props.theme.colors.textLight};
  max-width: 48rem;
  margin: 0 auto;
  line-height: 1.6;
`;


const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(21.875rem, 1fr));
  gap: 1.5rem;

  @media (min-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${props => props.theme.breakpoints.desktop}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const GalleryItemCard = styled(motion.div)`
  position: relative;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.md};
  cursor: pointer;
  aspect-ratio: 1;
  group: true;

  &:hover {
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const ProjectImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  ${GalleryItemCard}:hover & {
    transform: scale(1.1);
  }
`;

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
`;

const ContentOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.5rem;
  color: ${props => props.theme.colors.white};
`;

const ProjectTitle = styled.h3`
  font-family: ${props => props.theme.fonts.heading};
  font-size: 1.125rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.white};
`;

const CategoryBadge = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const HoverOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${GalleryItemCard}:hover & {
    opacity: 1;
  }
`;

const ViewIcon = styled.div`
  width: 4rem;
  height: 4rem;
  background: ${props => props.theme.colors.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: ${props => props.theme.colors.white};
`;


const CTASection = styled(motion.div)`
  text-align: center;
  margin-top: 3rem;
`;

const CTAButton = styled(motion.button)`
  background: ${props => props.theme.gradients.primary};
  color: ${props => props.theme.colors.white};
  font-weight: 600;
  font-size: 1.125rem;
  padding: 1rem 2rem;
  border: none;
  border-radius: 0.5rem;
  box-shadow: ${props => props.theme.shadows.lg};
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    transform: translateY(-0.125rem);
    box-shadow: ${props => props.theme.shadows.xl};
  }
`;

export const Gallery: React.FC = () => {
  const [selectedItem, setSelectedItem] = useState<ProjectGalleryItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleItemClick = (item: ProjectGalleryItem) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  const handleViewAllProjects = () => {
    const message = 'Hola, me interesa ver más proyectos de su portafolio completo. ¿Podrían mostrarme más trabajos realizados?';
    window.open(`https://wa.me/529513214264?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <GallerySection id="proyectos">
      <Container>
        <SectionHeader
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <SectionTitle>
            Galería de <span className="gradient-text">Trabajos</span>
          </SectionTitle>
          <SectionSubtitle>
            Descubre la calidad y el diseño de nuestros trabajos. Inspírate para tu próximo proyecto con nosotros.
          </SectionSubtitle>
        </SectionHeader>

        <GalleryGrid>
          {galleryItems.map((item, index) => (
            <GalleryItemCard
              key={item.id}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              onClick={() => handleItemClick(item)}
            >
              <ImageContainer>
                <ProjectImage src={item.imageUrl} alt={item.imageAlt} />
                <GradientOverlay />
                <ContentOverlay>
                  <CategoryBadge>{item.category}</CategoryBadge>
                  <ProjectTitle>{item.title}</ProjectTitle>
                </ContentOverlay>
                <HoverOverlay>
                  <ViewIcon><Icon name="Plus" size={28} /></ViewIcon>
                </HoverOverlay>
              </ImageContainer>
            </GalleryItemCard>
          ))}
        </GalleryGrid>

        <CTASection
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <CTAButton
            onClick={handleViewAllProjects}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Icon name="ClipboardList" size={18} />
            Ver Todos los Proyectos
          </CTAButton>
        </CTASection>
      </Container>

      <GalleryModal
        key={isModalOpen ? selectedItem?.id : 'closed'}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        item={selectedItem}
      />
    </GallerySection>
  );
};
